import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  ContentPaste as PasteIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TWButton, PageLayout } from '../../../shared/components';
import { postPasteClauses, postSplitClauses } from '../utils/contractApi';
import { colors, typography, textFieldStyles } from '../../../shared/styles/theme';
import ContractReviewProgressStepper from './ContractReviewProgressStepper';

const CONTRACT_TYPES = [
  { value: '', label: 'Select type (optional)' },
  { value: 'Contract', label: 'Contract' },
  { value: 'SOW', label: 'SOW' },
  { value: 'MSA', label: 'MSA' },
];

const MAX_BULK_CHARS = 20000;

const ClausePasteUpload: React.FC = () => {
  const navigate = useNavigate();

  const [clientName, setClientName] = useState('');
  const [contractType, setContractType] = useState('');
  const [isSigned, setIsSigned] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSubmitDisabled =
    isSubmitting ||
    !clientName.trim() ||
    !isSigned ||
    !bulkText.trim() ||
    bulkText.length > MAX_BULK_CHARS;

  const handleSubmit = async () => {
    const trimmedClient = clientName.trim();
    if (!trimmedClient) { setError('Please enter a client name.'); return; }
    if (!isSigned) { setError('Please select a contract status.'); return; }
    const trimmedText = bulkText.trim();
    if (!trimmedText) { setError('Please paste at least one clause.'); return; }

    setIsSubmitting(true);
    setError(null);
    try {
      const splitResult = await postSplitClauses(trimmedText);
      const clauses = splitResult.clauses.filter((c) => c.text.trim().length > 0);
      if (clauses.length === 0) {
        setError('AI could not identify any clauses in the pasted text. Please check the input.');
        return;
      }
      const response = await postPasteClauses({
        client_name: trimmedClient,
        contract_type: contractType.trim() || undefined,
        is_signed: isSigned === 'signed',
        clauses: clauses.map((c) => ({ text: c.text.trim() })),
      });
      navigate(`/contract-review/${response.session_uuid}`, {
        state: { uploadResult: response },
      });
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { detail?: string | Array<{ msg?: string }> } };
        message?: string;
      };
      const detail = ax.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg).filter(Boolean).join(', ')
            : ax.message;
      setError(msg ? `Submission failed: ${msg}` : 'Failed to submit clauses. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setClientName('');
    setContractType('');
    setIsSigned('');
    setBulkText('');
    setError(null);
  };

  const headerRightActions = (
    <Button
      variant="text"
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate('/contract-review')}
      sx={{
        color: colors.text.primary,
        textTransform: 'none',
        fontSize: typography.fontSize.base,
        fontWeight: 500,
        '&:hover': { backgroundColor: colors.background.hover },
      }}
    >
      Back to Contract Review
    </Button>
  );

  return (
    <PageLayout rightActions={headerRightActions}>
      <Box sx={{ mb: '24px', textAlign: 'left' }}>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            fontSize: typography.fontSize['2xl'],
            color: colors.text.primary,
            mb: '12px',
            lineHeight: 1.2,
          }}
        >
          Paste contract clauses
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: colors.text.secondary,
            fontSize: typography.fontSize.lg,
            mb: 0,
            lineHeight: 1.5,
          }}
        >
          Paste your contract clauses below. AI will automatically separate them when you submit.
        </Typography>
        <ContractReviewProgressStepper activeStep={1} />
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.95rem' } }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Contract metadata */}
      <Paper
        elevation={2}
        sx={{
          p: 4,
          mb: 3,
          borderRadius: 2,
          backgroundColor: colors.background.card,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <TextField
          fullWidth
          label="Client name"
          required
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder="Enter client or contract name"
          sx={{ mb: 2, ...textFieldStyles }}
        />
        <TextField
          fullWidth
          select
          label="Contract type"
          value={contractType}
          onChange={(e) => setContractType(e.target.value)}
          sx={{ mb: 3, ...textFieldStyles }}
        >
          {CONTRACT_TYPES.map((opt) => (
            <MenuItem key={opt.value || 'none'} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          select
          required
          label="Contract status"
          value={isSigned}
          onChange={(e) => setIsSigned(e.target.value)}
          sx={{ mb: 0, ...textFieldStyles }}
        >
          <MenuItem value="unsigned">Unsigned</MenuItem>
          <MenuItem value="signed">Signed</MenuItem>
        </TextField>
      </Paper>

      {/* Clause paste section */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${bulkText.trim() ? colors.primaryLight : colors.border.light}`,
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'border-color 0.15s',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.25,
            backgroundColor: colors.background.card,
            borderBottom: `1px solid ${colors.border.light}`,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 18, color: colors.primaryLight, mr: 1 }} />
          <Typography sx={{ flex: 1, fontWeight: 600, fontSize: typography.fontSize.sm, color: colors.text.primary }}>
            Paste multiple clauses at once
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={8}
            maxRows={20}
            placeholder="Paste all clauses here. AI will automatically identify and separate them when you submit."
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            disabled={isSubmitting}
            sx={{
              ...textFieldStyles,
              mb: 1,
              '& .MuiInputBase-input': { fontSize: typography.fontSize.sm, lineHeight: 1.6 },
            }}
          />
          <Typography
            sx={{
              fontSize: typography.fontSize.xs,
              color: bulkText.length > MAX_BULK_CHARS ? '#c62828' : colors.text.secondary,
            }}
          >
            {bulkText.length.toLocaleString()} / {MAX_BULK_CHARS.toLocaleString()} characters
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
        <TWButton
          label={isSubmitting ? 'Splitting & submitting...' : 'Submit clauses'}
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitDisabled}
          startIcon={!isSubmitting ? <PasteIcon /> : undefined}
          sx={{ fontSize: typography.fontSize.lg, minWidth: 220 }}
        />
        {(clientName || isSigned || bulkText) && (
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={isSubmitting}
            sx={{
              borderColor: colors.border.medium,
              color: colors.text.secondary,
              textTransform: 'none',
              fontSize: typography.fontSize.base,
              '&:hover': { borderColor: colors.border.dark },
            }}
          >
            Clear all
          </Button>
        )}
      </Box>
    </PageLayout>
  );
};

export default ClausePasteUpload;
